import React, { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppShell from '../components/layout/AppShell';
import { TableSkeleton } from '../components/ui/Skeleton';

// Each section is its own chunk, fetched when first opened: an owner on a
// phone opening Growth downloads Growth, not the whole console.
const TrafficDriversSection = lazy(() => import('../components/TrafficDriversSection'));
const SalesTransactionsSection = lazy(() => import('../components/SalesTransactionsSection'));
const AccountsSection = lazy(() => import('../components/AccountsSection'));
const SysDevOpsSection = lazy(() => import('../components/SysDevOpsSection'));
const ProductManagementSection = lazy(() => import('../components/ProductManagementSection'));
const InstagramAgentSection = lazy(() => import('../components/InstagramAgentSection'));
const StrategySection = lazy(() => import('../components/creativeEngine/StrategySection'));
const GrowthSection = lazy(() => import('../components/icepop/GrowthSection'));
import { DashboardFiltersProvider } from '../contexts/DashboardFilters.tsx';
import { resolveRoute } from '../config/navigation';
import apiService from '../services/api';
import SuperAdminLogin from '../components/SuperAdminLogin';
import sharedAuth from '../services/auth';

interface AdminData {
  id: string;
  username: string;
  email: string;
  role: string;
}

export interface QRCodeAnalytics {
  totalCodes: number;
  activeCodes: number;
  totalClicks: number;
  totalConversions: number;
  topPerformingChannel: string;
  recentActivity: Array<{
    code: string;
    channel: string;
    clicks: number;
    date: string;
  }>;
}

interface SuperAdminDashboardProps {
  testMode?: boolean;
  testUser?: AdminData | null;
}

const SuperAdminDashboard: React.FC<SuperAdminDashboardProps> = ({ testMode = false, testUser = null }) => {
  const navigate = useNavigate();
  const params = useParams<{ sectionKey?: string; tabKey?: string }>();

  // null while a stored token is being checked, so the login screen does not flash before the console.
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(() => (sharedAuth.getToken() ? null : false));
  const [adminData, setAdminData] = useState<AdminData | null>(null);
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);

  // Where we are in the IA. Seeded from the URL and pushed back to it, so a
  // refresh, a bookmark, or a link pasted into Slack all land on the same view
  // — previously every reload dropped the operator back on Traffic Drivers.
  const initial = resolveRoute(params.sectionKey, params.tabKey);
  const [sectionKey, setSectionKey] = useState(initial.section.key);
  const [tabKey, setTabKey] = useState(initial.tab.key);

  const [qrAnalytics, setQrAnalytics] = useState<QRCodeAnalytics | null>(null);
  const [qrError, setQrError] = useState<string | null>(null);
  const [isQrLoading, setIsQrLoading] = useState(true);

  const { section, tab } = resolveRoute(sectionKey, tabKey);

  // Browser back/forward changes the params; mirror them into state. Guarded
  // on a real match so the component still works when it is rendered outside a
  // parameterised route (tests, and the bare /superadmin entry point).
  useEffect(() => {
    if (!params.sectionKey) return;
    const resolved = resolveRoute(params.sectionKey, params.tabKey);
    setSectionKey(resolved.section.key);
    setTabKey(resolved.tab.key);
  }, [params.sectionKey, params.tabKey]);

  const handleNavigate = useCallback(
    (nextSectionKey: string, nextTabKey: string) => {
      const resolved = resolveRoute(nextSectionKey, nextTabKey);
      setSectionKey(resolved.section.key);
      setTabKey(resolved.tab.key);
      navigate(`/superadmin/${resolved.section.key}/${resolved.tab.key}`);
    },
    [navigate]
  );

  const loadQrAnalytics = useCallback(async () => {
    setIsQrLoading(true);
    setQrError(null);
    try {
      const response = await apiService.getQRAnalytics();
      if (!response.success || !response.analytics) {
        throw new Error(response.message || 'The QR analytics endpoint returned no data');
      }

      const analytics = response.analytics as {
        totalCodes?: number;
        activeCodes?: number;
        totalClicks?: number;
        totalConversions?: number;
        channelPerformance?: Array<{ _id: string; totalClicks: number }>;
      };

      // The endpoint returns channels sorted by clicks, so the leader is the
      // first entry. Left blank rather than guessed when nothing has been
      // scanned — the card renders an em dash for an empty value.
      const topChannel = analytics.channelPerformance?.[0]?._id ?? '';

      setQrAnalytics({
        totalCodes: analytics.totalCodes ?? 0,
        activeCodes: analytics.activeCodes ?? 0,
        totalClicks: analytics.totalClicks ?? 0,
        totalConversions: analytics.totalConversions ?? 0,
        topPerformingChannel: topChannel,
        // Per-code recent activity is not part of this endpoint. The QR
        // Tracking tab reads it from the code list instead; showing an
        // invented list here is what this replaces.
        recentActivity: [],
      });
    } catch (error) {
      // Previously this was swallowed into console.error and the KPI row
      // simply never appeared, which reads identically to "there is no data".
      console.error('Could not load QR analytics:', error);
      setQrError(error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setIsQrLoading(false);
    }
  }, []);

  // Only Traffic shows these numbers; the other sections do not pay for the call.
  useEffect(() => {
    if (sectionKey !== 'traffic-drivers' || qrAnalytics) return;
    void loadQrAnalytics();
  }, [loadQrAnalytics, sectionKey, qrAnalytics]);

  useEffect(() => {
    if (testMode && testUser) {
      setIsLoggedIn(true);
      setAdminData(testUser);
      return;
    }

    const checkAuth = async () => {
      const storedUser = sharedAuth.getUser();
      if (storedUser) {
        // Any authenticated admin. There is one role, and the page this used to
        // redirect the others to no longer exists.
        setIsLoggedIn(true);
        setAdminData(storedUser);
        return;
      }

      const token = sharedAuth.getToken();
      if (!token) {
        setIsLoggedIn(false);
        return;
      }

      try {
        const profile = await sharedAuth.verifyToken();
        if (profile.success && profile.user) {
          setIsLoggedIn(true);
          setAdminData(profile.user);
        } else {
          sharedAuth.removeToken();
          setIsLoggedIn(false);
        }
      } catch (error) {
        console.error('SuperAdminDashboard - Error checking authentication:', error);
        sharedAuth.removeToken();
        setIsLoggedIn(false);
      }
    };

    checkAuth();
  }, [testMode, testUser]);

  const handleLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsLoading(true);
    try {
      const response = await sharedAuth.login(loginData.username, loginData.password);
      if (response.success) {
        setIsLoggedIn(true);
        setAdminData(response.user);
      }
    } catch (error) {
      console.error('Login error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    sharedAuth.logout();
    setIsLoggedIn(false);
    setAdminData(null);
  };

  if (isLoggedIn === null) return <div className="min-h-screen bg-surface-sunken" aria-busy="true" />;
  if (!isLoggedIn) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-sunken">
        <SuperAdminLogin
          loginData={loginData}
          setLoginData={setLoginData}
          isLoading={isLoading}
          onSubmit={handleLogin}
        />
      </div>
    );
  }

  const renderSection = () => {
    switch (section.key) {
      case 'traffic-drivers':
        return (
          <TrafficDriversSection
            activeTab={tab.key}
            qrAnalytics={qrAnalytics}
            isLoading={isQrLoading}
            error={qrError}
            onRetry={loadQrAnalytics}
            setActiveTab={(nextTab: string) => handleNavigate('traffic-drivers', nextTab)}
          />
        );
      case 'sales':
        return (
          <SalesTransactionsSection
            activeTab={tab.key}
            setActiveTab={(nextTab: string) => handleNavigate('sales', nextTab)}
          />
        );
      case 'accounts':
        return <AccountsSection />;
      case 'sys-devops':
        return (
          <SysDevOpsSection
            activeTab={tab.key}
            setActiveTab={(nextTab: string) => handleNavigate('sys-devops', nextTab)}
          />
        );
      case 'product-management':
        return (
          <ProductManagementSection
            activeTab={tab.key}
            setActiveTab={(nextTab: string) => handleNavigate('product-management', nextTab)}
          />
        );
      case 'growth':
        return <GrowthSection activeTab={tab.key} />;
      case 'strategy':
        return <StrategySection activeTab={tab.key} />;
      case 'instagram-agent':
        return (
          <InstagramAgentSection
            activeTab={tab.key}
            setActiveTab={(nextTab: string) => handleNavigate('instagram-agent', nextTab)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <DashboardFiltersProvider>
      <AppShell
        adminData={adminData}
        onLogout={handleLogout}
        section={section}
        tab={tab}
        onNavigate={handleNavigate}
      >
        <Suspense fallback={<TableSkeleton rows={6} label="Loading" />}>{renderSection()}</Suspense>
      </AppShell>
    </DashboardFiltersProvider>
  );
};

export default SuperAdminDashboard;
